import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useEscape from 'next/hooks/use-escape';
import Portal from 'next/components/ui/lib/Portal';
import Fullscreen from './Fullscreen';

/**
 * HEY LISTEN! Portal needs to be imported via the full path so that
 * Jest can pick up the automock!
 */
// eslint-disable-next-line no-restricted-imports

import Fade from './lib/Fade';
import { IconButton } from './Icon';

export const P = styled.p`
  font-size: var(--regular);
`;

export const Heading = styled.h2`
  color: var(--heading-primary);
  font-size: var(--large);
  font-weight: var(--semi-bold);
  line-height: 1.5;
  margin: -8px 0 6px;
`;

export const Description = styled.span`
  display: inline-block;
  font-size: var(--regular);
  font-weight: var(--normal);
  line-height: 1.5;
  margin: 0 0 1em;
`;

export const Overflow = styled.div`
  overflow-y: auto;
`;

const CloseButton = styled(IconButton).attrs(() => ({ icon: 'times' }))`
  position: absolute;
  right: 26px;
  top: 26px;
`;

/**
 * Overlay backdrop between page and dialog to capture clicks
 *
 * @type {ReactElement}
 */
const Overlay = styled.div.attrs(() => ({
  role: 'button',
  'aria-label': 'overlay',
}))`
  background: var(--overlay);
  height: 100%;
  opacity: 0.8;
  position: absolute;
  width: 100%;
  z-index: 10001;
`;

/**
 * Centered dialog holding provided content
 *
 * @type {ReactElement}
 */
const Dialog = styled.div.attrs(() => ({ role: 'dialog' }))`
  background: var(--white);
  border-radius: 12px;
  box-shadow: var(--level-4);
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  max-height: 80%;
  min-height: 160px;
  padding: 32px;
  position: relative;
  z-index: 10002;

  width: ${({ size }) => {
    switch (size) {
      case 'xl':
        return '800px';
      case 'l':
        return '600px';
      case 'm':
        return '508px';
      default:
        return '400px';
    }
  }};

  ${({ size }) =>
    size === 'full' &&
    `
      border-radius: 0;
      height: 100%;
      max-height: 100%;
      width: 100%;

      ${Heading} {
        font-size: var(--x-large);
      }

      ${CloseButton} {
        font-size: var(--large);
      }
  `}
`;

/**
 * Modal component that mounts through a Portal. By default will show
 * immediately when mounted unless `visible` explicitly provided through
 * configurable fade-in
 *
 * @type {ReactElement}
 */
const Modal = ({
  children,
  className,
  closeOnEscape = true,
  onClose,
  size = 's',
  timeout = 200,
  visible = true,
}) => {
  /*
   * Disable body scroll when visible
   */
  useEffect(() => {
    if (!visible) {
      return () => {};
    }

    const {
      style: { overflow },
    } = document.body;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, [visible]);

  useEscape(visible && closeOnEscape && onClose);

  return (
    <Portal timeout={timeout}>
      <Fade visible={visible} timeout={timeout}>
        <Fullscreen>
          <Overlay onClick={onClose} />
          <Dialog className={className} size={size}>
            {onClose && (
              <CloseButton
                onClick={onClose}
                label="Dismiss dialog"
                title="Dismiss dialog"
              />
            )}
            {children}
          </Dialog>
        </Fullscreen>
      </Fade>
    </Portal>
  );
};

Modal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  closeOnEscape: PropTypes.bool,
  onClose: PropTypes.func,
  size: PropTypes.oneOf(['s', 'm', 'l', 'xl', 'full']),
  timeout: PropTypes.number,
  visible: PropTypes.bool,
};

export default styled(Modal)``;
